import { useEffect, useRef } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { UpdateToastView } from './UpdateToastView';

// A standalone PWA can stay resident for days without a navigation, so the
// browser never re-fetches sw.js on its own — the stale-bundle trap that
// used to need "Clear site data". Force the byte-check whenever the app
// comes back to the foreground, plus hourly while it stays open.
const UPDATE_INTERVAL_MS = 60 * 60 * 1000;

export function SWUpdateToast() {
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      registrationRef.current = registration ?? null;
    },
  });

  useEffect(() => {
    const check = () => {
      // update() rejects if the registration was torn down — never fatal.
      registrationRef.current?.update().catch(() => {});
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    const interval = setInterval(check, UPDATE_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(interval);
    };
  }, []);

  // updateServiceWorker(true) messages SKIP_WAITING and reloads once the new
  // SW takes control — precache swap and page reload happen together, so no
  // stale-page/new-cache mismatch.
  return <UpdateToastView show={needRefresh} onUpdate={() => updateServiceWorker(true)} />;
}
